import { type ReactElement, useState } from 'react'
import { type ConversationData, type ConversationId, isDefined, isEmpty } from '@terros/common'
import { Group, Paper, Stack, Text } from '@mantine/core'
import { useInfiniteConversations, useTranslation } from '@front/model'
import { Page, TAlert, TButton } from '@components'
import { ReviewConversation } from './ReviewConversation'
import { FlaggedQueueNotice } from './FlaggedQueueNotice'

export function FlaggedQueue(): ReactElement {
  const { t } = useTranslation()
  const queue = useInfiniteConversations({ reviewFlagged: true, filter: { statuses: ['Flagged'] } })
  const [selected, setSelected] = useState<ConversationData>()
  const [submitted, setSubmitted] = useState<ConversationId[]>([])
  const [notice, setNotice] = useState<string>()
  const visible = queue.data.filter((conversation) => !submitted.includes(conversation.convoId))

  function resolved(message: string): void {
    if (isDefined(selected)) setSubmitted((ids) => [...ids, selected.convoId])
    setNotice(message)
    setSelected(undefined)
    queue.refetch()
  }

  return (
    <Page>
      <Page.Heading title={t('Flagged conversations')} back isFetching={queue.isFetching} refetch={queue.refetch} />
      <Page.Content scrollArea>
        <Stack>
          <FlaggedQueueNotice />
          {isDefined(notice) && <TAlert variant='success'>{notice}</TAlert>}
          {isDefined(queue.error) && <TAlert variant='error'>{queue.error}</TAlert>}
          {queue.isSuccess && isEmpty(visible) && !queue.hasNextPage && (
            <Text>{t('No flagged conversations to review.')}</Text>
          )}
          {visible.map((conversation) => (
            <Paper key={conversation.convoId} withBorder p='md'>
              <Group justify='space-between'>
                <Stack gap='xs'>
                  <Text fw={600}>
                    {conversation.owner?.firstName} {conversation.owner?.lastName}
                  </Text>
                  <Text size='sm'>
                    {new Date(conversation.convoDate).toLocaleString()} · {Math.round(conversation.duration)}{' '}
                    {t('seconds')}
                  </Text>
                  <Text size='sm' c='dimmed'>
                    {conversation.address ?? conversation.convoId}
                  </Text>
                </Stack>
                <TButton onClick={() => setSelected(conversation)}>{t('Review recording')}</TButton>
              </Group>
            </Paper>
          ))}
          {queue.hasNextPage && (
            <TButton loading={queue.isFetchingNextPage} onClick={queue.fetchNextPage}>
              {t('Load more')}
            </TButton>
          )}
        </Stack>
      </Page.Content>
      {isDefined(selected) && (
        <ReviewConversation conversation={selected} onClose={() => setSelected(undefined)} onResolved={resolved} />
      )}
    </Page>
  )
}
